import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronRight, Lock, Unlock } from "lucide-react";
import { Fragment } from "react";
import { api, errorMessage, unwrap, type S } from "../../api/client";
import { Alert, Badge, Button, Card, PageHead, Segmented, Spinner, Switch, cx, useToast } from "../../components/ui";
import { FormattedMessage } from "react-intl";
import { useT } from "../../i18n/I18nProvider";
import type { MessageKey } from "../../i18n/keys";
import { DirIcon } from "../../i18n/mirror";

type Policy = "override" | "forced";
const POLICIES: { value: Policy; label: MessageKey }[] = [
  { value: "override", label: "admin.stages.policyOverride" },
  { value: "forced", label: "admin.stages.policyForced" },
];

/** Platform-wide pipeline: core stages, optional stages and their options, e.g. the obstacle detection choice (catalogue from the API). */
export function AdminStages() {
  const qc = useQueryClient();
  const toast = useToast();
  const t = useT();
  const stages = useQuery({ queryKey: ["stages"], queryFn: () => unwrap(api.GET("/v1/admin/stages")) });
  const switches = useQuery({ queryKey: ["pipeline-switches"], queryFn: () => unwrap(api.GET("/v1/admin/pipeline-switches")) });
  const update = useMutation({
    mutationFn: (body: S["PipelineSwitchesWrite"]) => unwrap(api.PUT("/v1/admin/pipeline-switches", { body })),
    onSuccess: (data) => {
      qc.setQueryData(["pipeline-switches"], data);
      qc.invalidateQueries({ queryKey: ["stages"] });
      qc.invalidateQueries({ queryKey: ["platform-settings"] });
      qc.invalidateQueries({ queryKey: ["org-pipeline"] });
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  if (stages.isLoading || switches.isLoading) return <Spinner />;
  if (!stages.data || !switches.data) return <Alert tone="danger">{errorMessage(stages.error ?? switches.error)}</Alert>;
  const items = switches.data.items;
  const allForced = items.every((s) => s.forced);
  const noneForced = items.every((s) => !s.forced);
  const set = (s: S["PipelineSwitch"], setting: S["PipelineSwitchSetting"], message: string) =>
    update.mutate({ switches: { [s.key]: setting } }, { onSuccess: () => toast(message) });

  const policy = (s: S["PipelineSwitch"]) => (
    <Segmented<Policy>
      value={s.forced ? "forced" : "override"}
      options={POLICIES.map((p) => ({ value: p.value, label: t(p.label) }))}
      onChange={(p) => set(s, { forced: p === "forced" }, t("admin.stages.policyChanged", { label: s.label, policy: p }))}
    />
  );
  const onOff = (s: S["PipelineSwitch"]) => (
    <>
      {policy(s)}
      <Switch checked={s.value === true} disabled={update.isPending} onChange={(on) => set(s, { value: on }, t("admin.stages.switched", { label: s.label, on: on ? "yes" : "no" }))} />
    </>
  );
  /** A choice switch: one value among its choices (single-select), with the selected value's warning. */
  const choice = (s: S["PipelineSwitch"]) => {
    const selected = s.choices.find((c) => c.value === s.value);
    return (
      <div className="switch-row sub" key={s.key}>
        <div style={{ flex: 1 }}>
          <div className="row" style={{ gap: 8 }}>
            <b>{s.label}</b>
            <code className="faint" dir="ltr">{s.key}</code>
            {selected?.legacy && <Badge tone="warn">{t("admin.stages.legacy")}</Badge>}
          </div>
          <div className="small muted" style={{ marginBlockStart: 2 }}>{s.description}</div>
          <div className="stack" style={{ marginBlockStart: 8, gap: 6 }} role="radiogroup" aria-label={s.label}>
            {s.choices.map((c) => (
              <label key={c.value} className="row" style={{ gap: 8, alignItems: "flex-start", cursor: "pointer" }}>
                <input
                  type="radio"
                  name={s.key}
                  checked={c.value === s.value}
                  disabled={update.isPending}
                  onChange={() => set(s, { value: c.value }, t("admin.stages.choiceChanged", { label: s.label, choice: c.label }))}
                />
                <span>
                  <b>{c.label}</b> {c.legacy && <Badge plain>{t("admin.stages.legacy")}</Badge>}
                  {c.value === s.default && <Badge plain>{t("admin.stages.default")}</Badge>}
                  <span className="small muted" style={{ display: "block" }}>{c.description}</span>
                </span>
              </label>
            ))}
          </div>
          {selected?.warning && (
            <div style={{ marginBlockStart: 8 }}>
              <Alert tone="warn">{selected.warning}</Alert>
            </div>
          )}
        </div>
        {policy(s)}
      </div>
    );
  };
  const flag = (s: S["PipelineSwitch"], parentOff: boolean) => (
    <div className={cx("switch-row", "sub", (s.value !== true || parentOff) && "off")} key={s.key}>
      <div>
        <div className="row" style={{ gap: 8 }}>
          <b>{s.label}</b>
          <code className="faint" dir="ltr">{s.key}</code>
          {s.default !== true && <Badge plain>{t("admin.stages.defaultOff")}</Badge>}
        </div>
        <div className="small muted" style={{ marginBlockStart: 2 }}>{s.description}</div>
      </div>
      {onOff(s)}
    </div>
  );

  return (
    <>
      <PageHead
        title={t("admin.stages.title")}
        description={t("admin.stages.description")}
        actions={
          <>
            <Button icon={<Lock />} disabled={allForced || update.isPending} onClick={() => update.mutate({ force_all: true }, { onSuccess: () => toast(t("admin.stages.forcedAll")) })}>
              {t("admin.stages.forceAll")}
            </Button>
            <Button icon={<Unlock />} disabled={noneForced || update.isPending} onClick={() => update.mutate({ force_all: false }, { onSuccess: () => toast(t("admin.stages.allowedAll")) })}>
              {t("admin.stages.allowAll")}
            </Button>
          </>
        }
      />
      <div className="stack">
        <Card pad={false}>
          <div className="card-body">
            <div className="pipeline-flow">
              {stages.data.items.map((s, i) => (
                <Fragment key={s.stage}>
                  {i > 0 && <DirIcon><ChevronRight /></DirIcon>}
                  <span className={cx("node", !s.enabled && "off")}>{s.label}</span>
                </Fragment>
              ))}
            </div>
          </div>
        </Card>
        <Card pad={false}>
          <div className="stage-list">
            {stages.data.items.map((s, i) => {
              const own = items.find((d) => d.kind === "stage" && d.key === s.stage);
              const owner = items.find((d) => d.key === s.switch_key && d.kind === "choice");
              const options = items.filter((d) => d.kind !== "stage" && d.stage === s.stage);
              return (
                <div key={s.stage} className={cx("stage-block", s.optional && "optional", !s.enabled && "off")}>
                  <div className={cx("stage-item", s.optional && "optional", !s.enabled && "off")}>
                    <div className="idx">{i + 1}</div>
                    <div>
                      <div className="row" style={{ gap: 8 }}>
                        <b>{s.label}</b>
                        <code className="faint" dir="ltr">{s.stage}</code>
                        {s.optional ? <Badge tone={s.enabled ? "ok" : "warn"}>{s.enabled ? t("admin.stages.on") : t("admin.stages.off")}</Badge> : null}
                        {(own ?? owner)?.forced && <Badge tone="info">{t("admin.stages.forced")}</Badge>}
                      </div>
                      <div className="small muted" style={{ marginBlockStart: 2 }}>{s.description}</div>
                    </div>
                    {own ? (
                      <div className="row" style={{ gap: 14 }}>{onOff(own)}</div>
                    ) : owner ? (
                      <span className="small muted">{t("admin.stages.switchedBy", { label: owner.label })}</span>
                    ) : (
                      <span className="lock" title={t("admin.stages.coreTitle")}>
                        <Lock /> {t("admin.stages.core")}
                      </span>
                    )}
                  </div>
                  {options.length > 0 && <div className="switch-group-head">{t("admin.stages.options")}</div>}
                  {options.map((d) => (d.kind === "choice" ? choice(d) : flag(d, !s.enabled)))}
                </div>
              );
            })}
          </div>
        </Card>
        <p className="hint">{t("admin.stages.hintObstacles")}</p>
        <p className="hint">
          <FormattedMessage
            id="admin.stages.hintStorage"
            values={{ stagesKey: "disabled_stages", switchesKey: "pipeline_switches", code: (c) => <code dir="ltr">{c}</code> }}
          />
        </p>
      </div>
    </>
  );
}
